import { useCallback, useMemo } from "react"
import { IconButton, Tooltip } from "@mui/material"
import TuneIcon from "@mui/icons-material/Tune"
import { useHasResourcePermission } from "hooks/permissions"
import { PERMISSION_LEVELS, type PermissionLevel } from "helpers/permissions"
import GraphTemplateSelector from "connected-components/graph/components/template-selector"
import GraphPanelPeriodControl from "connected-components/graph/components/panel-period-control"
import { useTemplateMetadata } from "resources/templates"
import { useGraphData } from "../../hooks/use-graph-data"
import { decodeFamilyMetricIds, resolveCustomGraphFamilyContext } from "./family"

interface CustomGraphHeaderProps {
  templateMetadata?: { permissionLevel?: PermissionLevel, config?: { metrics?: string[] | null } }
}

export default function CustomGraphHeader({ templateMetadata }: CustomGraphHeaderProps) {
  const { showMetricsSelector, setShowMetricsSelector, duplicateFromTemplateId, duplicateReturnViewId } = useGraphData()
  const [duplicateSourceMetadata] = useTemplateMetadata(duplicateFromTemplateId ?? undefined)

  const canEditMetrics = useHasResourcePermission(templateMetadata, PERMISSION_LEVELS.WRITE)

  const familyContext = useMemo(() => resolveCustomGraphFamilyContext({
    decodedTemplateMetrics: decodeFamilyMetricIds(templateMetadata?.config?.metrics),
    duplicateFromTemplateId,
    decodedDuplicateSourceMetrics: decodeFamilyMetricIds(duplicateSourceMetadata?.config?.metrics),
    duplicateReturnViewId,
  }), [templateMetadata, duplicateFromTemplateId, duplicateSourceMetadata, duplicateReturnViewId])
  const isPerformance = familyContext?.family === "performance"

  const handleOpenMetricSelector = useCallback(() => {
    setShowMetricsSelector(!showMetricsSelector)
  }, [showMetricsSelector, setShowMetricsSelector])

  return (
    <>
      <GraphTemplateSelector />
      <GraphPanelPeriodControl isPerformance={isPerformance} />
      {(!templateMetadata || canEditMetrics) && <Tooltip title="Select metrics">
        <IconButton onClick={handleOpenMetricSelector} size="small">
          <TuneIcon fontSize="small" />
        </IconButton>
      </Tooltip>}
    </>
  )
}
